import React, { useState, useRef, Ref, useImperativeHandle } from 'react';
import { useEditor, EditorContent, useEditorState } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import ResizeImage from 'tiptap-extension-resize-image';
import { Table as TableExtension } from '@tiptap/extension-table';
import TableRowExtension from '@tiptap/extension-table-row';
import TableHeaderExtension from '@tiptap/extension-table-header';
import TableCellExtension from '@tiptap/extension-table-cell';
import { Toolbar } from 'radix-ui';
import {
  BoldIcon,
  ItalicIcon,
  UnderlineIcon,
  StrikethroughIcon,
  Heading1Icon,
  Heading2Icon,
  Heading3Icon,
  PilcrowIcon,
  ListIcon,
  ListOrderedIcon,
  TableIcon,
  LinkIcon,
  UnlinkIcon,
  ImageIcon,
  UndoIcon,
  RedoIcon,
  ArrowUpToLineIcon,
  ArrowDownToLineIcon,
  Trash2Icon,
  ArrowLeftToLineIcon,
  ArrowRightToLineIcon
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '../ui/dropdown-menu';
import { Button, buttonVariants } from '../ui/button';
import { Input } from '../ui/input';
import { T } from '@transifex/react';

import { cn } from '../../lib/utils';

enum TextBlockType {
  Paragraph = 'paragraph',
  H1 = 'h1',
  H2 = 'h2',
  H3 = 'h3'
}

enum TextMarkType {
  Bold = 'bold',
  Italic = 'italic',
  Underline = 'underline',
  Strike = 'strike'
}

enum ListType {
  Bullet = 'bullet',
  Ordered = 'ordered'
}

const ToolbarSeparator = () => (
  <Toolbar.Separator className="mx-1 h-4 w-px bg-border" />
);

interface RichTextEditorRef {
  focus: () => void;
}

export interface RichTextEditorProps {
  ref?: Ref<RichTextEditorRef>;
  content?: string;
  onChange?: (html: string) => void;
  onImageUpload?: (file: File) => Promise<string>;
  className?: string;
  disabled?: boolean;
  'aria-invalid'?: boolean | 'true' | 'false';
}

export function RichTextEditor({
  ref,
  content = '',
  onChange,
  onImageUpload,
  className,
  disabled = false,
  'aria-invalid': ariaInvalid
}: RichTextEditorProps) {
  const [linkUrl, setLinkUrl] = useState('');
  const [isLinkMenuOpen, setIsLinkMenuOpen] = useState(false);
  const [isTableMenuOpen, setIsTableMenuOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const editor = useEditor({
    editable: !disabled,
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3]
        }
      }),
      ResizeImage,
      TableExtension.configure({
        resizable: true
      }),
      TableRowExtension,
      TableHeaderExtension,
      TableCellExtension
    ],
    content,
    editorProps: {
      attributes: {
        class: cn(
          'prose prose-sm dark:prose-invert max-w-none focus:outline-none min-h-[140px] px-3 py-2 text-base md:text-sm',
          'selection:bg-primary selection:text-primary-foreground placeholder:text-muted-foreground',
          '[&_table]:w-full [&_table]:border-collapse [&_table]:my-2 [&_td]:border [&_td]:border-border [&_td]:p-2 [&_th]:border [&_th]:border-border [&_th]:p-2 [&_th]:bg-muted',
          '[&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-2 [&_li]:my-0.5',
          '[&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-md [&_img]:my-2',
          '[&_a]:underline'
        )
      }
    },
    onUpdate: ({ editor }) => {
      onChange?.(editor.isEmpty ? '' : editor.getHTML());
    }
  });

  useImperativeHandle(
    ref,
    () => ({
      focus: () => {
        editor?.chain().focus().run();
      }
    }),
    [editor]
  );

  const activeBlock = useEditorState({
    editor,
    selector: (ctx) => {
      if (ctx.editor.isActive('heading', { level: 1 })) return TextBlockType.H1;
      if (ctx.editor.isActive('heading', { level: 2 })) return TextBlockType.H2;
      if (ctx.editor.isActive('heading', { level: 3 })) return TextBlockType.H3;
      return TextBlockType.Paragraph;
    }
  });

  const activeMarks = useEditorState({
    editor,
    selector: (ctx) => {
      const marks: Array<string> = [];
      if (ctx.editor.isActive(TextMarkType.Bold)) marks.push(TextMarkType.Bold);
      if (ctx.editor.isActive(TextMarkType.Italic))
        marks.push(TextMarkType.Italic);
      if (ctx.editor.isActive(TextMarkType.Underline))
        marks.push(TextMarkType.Underline);
      if (ctx.editor.isActive(TextMarkType.Strike))
        marks.push(TextMarkType.Strike);
      return marks;
    }
  });

  const activeList = useEditorState({
    editor,
    selector: (ctx) => {
      if (ctx.editor.isActive('bulletList')) return ListType.Bullet;
      else if (ctx.editor.isActive('orderedList')) return ListType.Ordered;
      return undefined;
    }
  });

  if (!editor) {
    return null;
  }

  const getButtonClass = (isActive = false) =>
    cn(
      buttonVariants({
        variant: isActive ? 'secondary' : 'ghost',
        size: 'icon-sm'
      }),
      isActive && 'bg-neutral-alpha-200 text-neutral-900'
    );

  const handleSetLink = (e: React.FormEvent) => {
    e.preventDefault();
    if (!linkUrl.trim()) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: linkUrl })
        .run();
    }
    setLinkUrl('');
    setIsLinkMenuOpen(false);
  };

  const handleImageFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (onImageUpload) {
      const src = await onImageUpload(file);
      if (src) {
        editor.chain().focus().setImage({ src }).run();
      }
    } else {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          editor.chain().focus().setImage({ src: reader.result }).run();
        }
      };
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  const handleListToggle = (value: string) => {
    const target = value || activeList;

    if (target === ListType.Bullet) {
      editor.chain().focus().toggleBulletList().run();
    } else if (target === ListType.Ordered) {
      editor.chain().focus().toggleOrderedList().run();
    }
  };

  return (
    <div
      aria-invalid={ariaInvalid}
      className={cn(
        'w-full min-w-0 rounded-md border border-input bg-background shadow-none transition-[color,box-shadow]',
        'focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50',
        'aria-invalid:border-destructive aria-invalid:ring-destructive/20',
        disabled && 'pointer-events-none cursor-not-allowed opacity-50',
        className
      )}
    >
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleImageFileChange}
        accept="image/*"
        className="hidden"
      />
      <Toolbar.Root
        className="flex flex-wrap items-center gap-1 border-b border-input bg-muted/30 p-1"
        aria-label="Formatting options"
      >
        <Toolbar.ToggleGroup
          type="single"
          value={activeBlock}
          onValueChange={(value) => {
            if (!value) return;
            if (value === TextBlockType.Paragraph)
              editor.chain().focus().setParagraph().run();
            if (value === TextBlockType.H1)
              editor.chain().focus().toggleHeading({ level: 1 }).run();
            if (value === TextBlockType.H2)
              editor.chain().focus().toggleHeading({ level: 2 }).run();
            if (value === TextBlockType.H3)
              editor.chain().focus().toggleHeading({ level: 3 }).run();
          }}
          aria-label="Text formatting level"
          className="flex items-center gap-0.5"
        >
          <Toolbar.ToggleItem
            value={TextBlockType.Paragraph}
            aria-label="Paragraph"
            className={getButtonClass(
              editor.isActive(TextBlockType.Paragraph) &&
                !editor.isActive('heading')
            )}
          >
            <PilcrowIcon />
          </Toolbar.ToggleItem>
          <Toolbar.ToggleItem
            value={TextBlockType.H1}
            aria-label="Heading 1"
            className={getButtonClass(editor.isActive('heading', { level: 1 }))}
          >
            <Heading1Icon />
          </Toolbar.ToggleItem>
          <Toolbar.ToggleItem
            value={TextBlockType.H2}
            aria-label="Heading 2"
            className={getButtonClass(editor.isActive('heading', { level: 2 }))}
          >
            <Heading2Icon />
          </Toolbar.ToggleItem>
          <Toolbar.ToggleItem
            value={TextBlockType.H3}
            aria-label="Heading 3"
            className={getButtonClass(editor.isActive('heading', { level: 3 }))}
          >
            <Heading3Icon />
          </Toolbar.ToggleItem>
        </Toolbar.ToggleGroup>
        <ToolbarSeparator />
        <Toolbar.ToggleGroup
          type="multiple"
          value={activeMarks}
          aria-label="Text formatting"
          className="flex items-center gap-0.5"
        >
          <Toolbar.ToggleItem
            value={TextMarkType.Bold}
            aria-label="Toggle bold"
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={getButtonClass(activeMarks.includes(TextMarkType.Bold))}
          >
            <BoldIcon />
          </Toolbar.ToggleItem>
          <Toolbar.ToggleItem
            value={TextMarkType.Italic}
            aria-label="Toggle italic"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={getButtonClass(
              activeMarks.includes(TextMarkType.Italic)
            )}
          >
            <ItalicIcon />
          </Toolbar.ToggleItem>
          <Toolbar.ToggleItem
            value={TextMarkType.Underline}
            aria-label="Toggle underline"
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            className={getButtonClass(
              activeMarks.includes(TextMarkType.Underline)
            )}
          >
            <UnderlineIcon />
          </Toolbar.ToggleItem>
          <Toolbar.ToggleItem
            value={TextMarkType.Strike}
            aria-label="Toggle strikethrough"
            onClick={() => editor.chain().focus().toggleStrike().run()}
            className={getButtonClass(
              activeMarks.includes(TextMarkType.Strike)
            )}
          >
            <StrikethroughIcon />
          </Toolbar.ToggleItem>
        </Toolbar.ToggleGroup>
        <ToolbarSeparator />
        <div className="flex items-center gap-0.5">
          <Toolbar.ToggleGroup
            type="single"
            value={activeList ?? ''}
            onValueChange={handleListToggle}
            aria-label="List options"
            className="flex items-center gap-0.5"
          >
            <Toolbar.ToggleItem
              value={ListType.Bullet}
              aria-label="Bullet list"
              className={getButtonClass(activeList === ListType.Bullet)}
            >
              <ListIcon />
            </Toolbar.ToggleItem>
            <Toolbar.ToggleItem
              value={ListType.Ordered}
              aria-label="Numbered list"
              className={getButtonClass(activeList === ListType.Ordered)}
            >
              <ListOrderedIcon />
            </Toolbar.ToggleItem>
          </Toolbar.ToggleGroup>
          <DropdownMenu
            open={isTableMenuOpen}
            onOpenChange={setIsTableMenuOpen}
          >
            <DropdownMenuTrigger asChild>
              <Toolbar.Button
                aria-label="Table options"
                className={getButtonClass(editor.isActive('table'))}
              >
                <TableIcon />
              </Toolbar.Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {!editor.isActive('table') ? (
                <DropdownMenuItem
                  onSelect={() => {
                    editor
                      .chain()
                      .focus()
                      .insertTable({
                        rows: 3,
                        cols: 3,
                        withHeaderRow: true
                      })
                      .run();
                    setIsTableMenuOpen(false);
                  }}
                >
                  <TableIcon />
                  <T _str="Insert table (3x3)" />
                </DropdownMenuItem>
              ) : (
                <DropdownMenuGroup>
                  <DropdownMenuItem
                    onSelect={() => {
                      editor.chain().focus().addRowBefore().run();
                      setIsTableMenuOpen(false);
                    }}
                  >
                    <ArrowUpToLineIcon />
                    <T _str="Add row above" />
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() => {
                      editor.chain().focus().addRowAfter().run();
                      setIsTableMenuOpen(false);
                    }}
                  >
                    <ArrowDownToLineIcon />
                    <T _str="Add row below" />
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() => {
                      editor.chain().focus().deleteRow().run();
                      setIsTableMenuOpen(false);
                    }}
                    variant="destructive"
                  >
                    <Trash2Icon />
                    <T _str="Delete row" />
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={() => {
                      editor.chain().focus().addColumnBefore().run();
                      setIsTableMenuOpen(false);
                    }}
                  >
                    <ArrowLeftToLineIcon />
                    <T _str="Add column left" />
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() => {
                      editor.chain().focus().addColumnAfter().run();
                      setIsTableMenuOpen(false);
                    }}
                  >
                    <ArrowRightToLineIcon />
                    <T _str="Add column right" />
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() => {
                      editor.chain().focus().deleteColumn().run();
                      setIsTableMenuOpen(false);
                    }}
                    variant="destructive"
                  >
                    <Trash2Icon />
                    <T _str="Delete column" />
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={() => {
                      editor.chain().focus().deleteTable().run();
                      setIsTableMenuOpen(false);
                    }}
                    variant="destructive"
                  >
                    <Trash2Icon />
                    <T _str="Delete table" />
                  </DropdownMenuItem>
                </DropdownMenuGroup>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <ToolbarSeparator />
        <div className="flex items-center gap-0.5">
          <DropdownMenu open={isLinkMenuOpen} onOpenChange={setIsLinkMenuOpen}>
            <DropdownMenuTrigger asChild>
              <Toolbar.Button
                aria-label="Link options"
                className={getButtonClass(editor.isActive('link'))}
              >
                <LinkIcon />
              </Toolbar.Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="p-2">
              <form onSubmit={handleSetLink} className="flex gap-2">
                <Input
                  type="url"
                  placeholder="https://example.com"
                  value={linkUrl || editor.getAttributes('link').href}
                  onChange={(e) => setLinkUrl(e.target.value)}
                />
                <Button type="submit" variant="secondary">
                  <T _str="Set" />
                </Button>
                {editor.isActive('link') && (
                  <Button
                    onClick={() => {
                      editor.chain().focus().unsetLink().run();
                      setIsLinkMenuOpen(false);
                    }}
                    variant="destructive-secondary"
                    size="icon"
                  >
                    <UnlinkIcon />
                  </Button>
                )}
              </form>
            </DropdownMenuContent>
          </DropdownMenu>
          <Toolbar.Button
            aria-label="Upload image"
            onClick={() => fileInputRef.current?.click()}
            className={getButtonClass()}
          >
            <ImageIcon />
          </Toolbar.Button>
        </div>
        <ToolbarSeparator />
        <div className="ml-auto flex items-center gap-0.5">
          <Toolbar.Button
            aria-label="Undo"
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
            className={cn(
              getButtonClass(),
              'disabled:opacity-40 disabled:hover:bg-transparent'
            )}
          >
            <UndoIcon />
          </Toolbar.Button>
          <Toolbar.Button
            aria-label="Redo"
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
            className={cn(
              getButtonClass(),
              'disabled:opacity-40 disabled:hover:bg-transparent'
            )}
          >
            <RedoIcon />
          </Toolbar.Button>
        </div>
      </Toolbar.Root>
      <EditorContent editor={editor} />
    </div>
  );
}
